import React, { FC } from 'react';

import { styled } from '@mui/material';
import { css } from '@mui/material/styles';

import LockOpenedIcon from '~/assets/icons/LockOpenedIcon';
import IKImage from '~/components/IKImage/IKImage';
import JoinNowBlock from '~/components/JoinNowBlock/JoinNowBlock';
import { JoinNowBlockProps } from '~/components/JoinNowBlock/JoinNowBlock';
import { useTenantContext } from '~/context/TenantProvider';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { MIDDLE_MARGIN_PX, TINY_MARGIN_PX } from '~/theme';
import env from '~/util/env';
import { TenantsRefs } from '~/utils/tenantsConfig';

interface JoinNowProps extends JoinNowBlockProps {
  availableForFree: boolean;
  showForDanske?: boolean;
  className?: string;
}

const JoinNow: FC<JoinNowProps> = ({
  showForDanske = false,
  loading,
  availableForFree,
  className,
  ...rest
}) => {
  const { config, tenant } = useTenantContext();
  const { t } = useTenantTranslation();

  if (env.isProd() ? tenant?.name === TenantsRefs.Su && showForDanske : showForDanske) {
    return (
      <JoinNowBlock
        loading={loading}
        title={t('block.joinNowBlock.approachToInvestment')}
        description={t('block.joinNowBlock.visitOurWebsite')}
        btnCaption={t('button.learnMore')}
        Icon={(...props) => <Logo path="/logos/Danske_Bank_Logo_White_RGB%201.png" {...props} />}
        linkProps={{
          to: 'https://danskebank.com/sustainability/publications-and-policies/sustainability-related-disclosures',
          target: '_blank',
        }}
        additionalCss={{
          lockTextCss: danskeLockTextCss,
          descriptionCss: danskeDescCss,
        }}
        className={className}
      />
    );
  }

  const joinNowConfig = config?.joinNowBlock;
  const joinNowBlockTitle = availableForFree ? t(joinNowConfig?.freeVideoTitleKey) : undefined;
  const joinNowBlockDescription = availableForFree
    ? t(joinNowConfig?.freeVideoDescriptionKey)
    : undefined;

  return (
    <JoinNowBlock
      loading={loading}
      title={joinNowBlockTitle}
      description={joinNowBlockDescription}
      data-testid="joinNowBlock"
      Icon={availableForFree ? LockOpenedIcon : undefined}
      className={className}
      {...rest}
    />
  );
};

export default JoinNow;

const Logo = styled(IKImage)`
  width: 170px;
  height: 30px;
  max-height: 170px;
  margin-bottom: ${TINY_MARGIN_PX};
  object-fit: contain;

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-bottom: 0;
  }
`;
const danskeLockTextCss = css`
  display: none;
`;
const danskeDescCss = ({ theme }) => css`
  ${theme.breakpoints.up('md')} {
    margin-right: ${MIDDLE_MARGIN_PX};
    margin-left: ${MIDDLE_MARGIN_PX};
  }
`;
